import {
  INITIAL_VITALS,
  UnitMapping,
  Vitals,
  VitalsEnumIndex,
} from "./vital-constant";
import BloodPressureFields from "./vital-sections/BloodPressureFields";
import BmiFields from "./vital-sections/BmiFields";
import ForceExpiratoryVolumeFields from "./vital-sections/ForceExpiratoryVolumeFields";
import GlucoseFields from "./vital-sections/GlucoseFields";
import HeightFields from "./vital-sections/HeightFields";
import OxygenFields from "./vital-sections/OxygenFields";
import PeakExpiratoryFlow from "./vital-sections/PeakExpiratoryFlow";
import PerfusionIndexFields from "./vital-sections/PerfusionIndexFields";
import PulseRateFields from "./vital-sections/PulseRateFields";
import RespirationFields from "./vital-sections/RespirationFields";
import RespiratoryVolumeFields from "./vital-sections/RespiratoryVolumeFields";
import WeightFields from "./vital-sections/WeightFields";

import { Grid } from "@mui/material";
import { memo } from "react";
import Text from "../../../../../../../components/Text";
import { VitalFormik } from "./vital-constant";
import HeartRateFields from "./vital-sections/HeartRateFields";
import TemperatureFields from "./vital-sections/TemperatureFields";

interface AllVitalsProps {
  formik: VitalFormik;
}

const AllVitals = memo(({ formik }: AllVitalsProps) => {
  const getIndex = (k: Vitals) => VitalsEnumIndex[k];

  const activeVital = formik.values.patientVitals;

  const getUnitDisplay = (k: Vitals) => {
    const vital = activeVital?.[getIndex(k)];
    if (k === "bodyTemperature") {
      return vital?.unit === "c" ? "°C" : "°F";
    }
    return UnitMapping[vital?.name as Vitals] ?? "";
  };

  const getTitle = (k: Vitals) => {
    return (
      <Text fontWeight="medium" mb={1}>
        {INITIAL_VITALS?.[getIndex(k)]?.label}
        {activeVital?.[getIndex(k)]?.selected && `(${getUnitDisplay(k)})`}
      </Text>
    );
  };

  const isVitalSelected = (k: Vitals) => activeVital?.[getIndex(k)]?.selected;

  return (
    <Grid container gap={2} width={"100%"}>
      <Grid container gap={2}>
        {isVitalSelected("bloodPressure") && (
          <Grid key={"bloodPressure"} xs={12}>
            {getTitle("bloodPressure")}

            <BloodPressureFields
              formik={formik}
              ind={getIndex("bloodPressure")}
            />
          </Grid>
        )}

        {isVitalSelected("bloodGlucose") && (
          <Grid key={"bloodGlucose"} xs={12}>
            {getTitle("bloodGlucose")}

            <GlucoseFields formik={formik} ind={getIndex("bloodGlucose")} />
          </Grid>
        )}

        {isVitalSelected("bodyTemperature") && (
          <Grid key={"bodyTemperature"} xs={12}>
            {getTitle("bodyTemperature")}

            <TemperatureFields
              formik={formik}
              ind={getIndex("bodyTemperature")}
            />
          </Grid>
        )}

        {/* heart & respiration Rate */}
        <Grid container gap={2}>
          {isVitalSelected("heartRate") && (
            <Grid key={"heartRate"} xs={5.9}>
              {getTitle("heartRate")}
              <HeartRateFields formik={formik} ind={getIndex("heartRate")} />
            </Grid>
          )}

          {isVitalSelected("respirationRate") && (
            <Grid key={"respirationRate"} xs={5.9}>
              {getTitle("respirationRate")}
              <RespirationFields
                formik={formik}
                ind={getIndex("respirationRate")}
              />
            </Grid>
          )}
        </Grid>

        {isVitalSelected("height") && (
          <Grid key={"height"} xs={12}>
            {getTitle("height")}

            <HeightFields formik={formik} ind={getIndex("height")} />
          </Grid>
        )}

        {isVitalSelected("weight") && (
          <Grid key={"weight"} xs={12}>
            {getTitle("weight")}

            <WeightFields formik={formik} ind={getIndex("weight")} />
          </Grid>
        )}

        {isVitalSelected("o2_saturation") && (
          <Grid key={"o2_saturation"} xs={12}>
            {getTitle("o2_saturation")}

            <OxygenFields formik={formik} ind={getIndex("o2_saturation")} />
          </Grid>
        )}

        {isVitalSelected("pulseRate") && (
          <Grid key={"pulseRate"} xs={12}>
            {getTitle("pulseRate")}

            <PulseRateFields formik={formik} ind={getIndex("pulseRate")} />
          </Grid>
        )}

        {isVitalSelected("bmi") && (
          <Grid key={"bmi"} xs={12}>
            {getTitle("bmi")}

            <BmiFields formik={formik} ind={getIndex("bmi")} />
          </Grid>
        )}

        {isVitalSelected("respiratoryVolume") && (
          <Grid key={"respiratoryVolume"} xs={12}>
            {getTitle("respiratoryVolume")}

            <RespiratoryVolumeFields
              formik={formik}
              ind={getIndex("respiratoryVolume")}
            />
          </Grid>
        )}

        {/* perfusionIndex, peakExpiratoryFlow & forceExpiratoryVolume  */}
        <Grid container gap={2} xs={12}>
          {isVitalSelected("perfusionIndex") && (
            <Grid key={"perfusionIndex"} xs={2.6}>
              {getTitle("perfusionIndex")}

              <PerfusionIndexFields
                formik={formik}
                ind={getIndex("perfusionIndex")}
              />
            </Grid>
          )}

          {isVitalSelected("peakExpiratoryFlow") && (
            <Grid key={"peakExpiratoryFlow"} xs={2.6}>
              {getTitle("peakExpiratoryFlow")}

              <PeakExpiratoryFlow
                formik={formik}
                ind={getIndex("peakExpiratoryFlow")}
              />
            </Grid>
          )}

          {isVitalSelected("forceExpiratoryVolume") && (
            <Grid key={"forceExpiratoryVolume"} xs={2.6}>
              {getTitle("forceExpiratoryVolume")}

              <ForceExpiratoryVolumeFields
                formik={formik}
                ind={getIndex("forceExpiratoryVolume")}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
});

export default AllVitals;
